import React, { useEffect, useMemo, useState } from "react";
import api from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Popover, PopoverTrigger, PopoverContent } from "../../components/ui/popover";
import { Calendar } from "../../components/ui/calendar";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Plus, CalendarDays } from "lucide-react";
import { format, addDays, startOfMonth, endOfMonth, differenceInDays, addMonths, parseISO } from "date-fns";
import { ptBR } from "date-fns/locale";

function iso(d) {
  return format(d, "yyyy-MM-dd");
}

const TYPE_LABEL = {
  reservation: "Reserva",
  manual: "Manual",
  airbnb: "Airbnb",
  booking: "Booking",
  other: "Outro",
};

const TYPE_COLOR = {
  reservation: "bg-[#0AA89C]",
  manual: "bg-slate-500",
  airbnb: "bg-rose-500",
  booking: "bg-blue-500",
  other: "bg-amber-500",
};

export default function AdminCalendar() {
  const [houses, setHouses] = useState([]);
  const [blockings, setBlockings] = useState([]);
  const [monthStart, setMonthStart] = useState(startOfMonth(new Date()));
  const [showBlock, setShowBlock] = useState(false);
  const [newBlock, setNewBlock] = useState({ house_id: "", range: { from: undefined, to: undefined }, type: "manual", note: "" });

  async function load() {
    const h = await api.get("/admin/houses");
    setHouses(h.data);
    const b = await api.get("/admin/blockings");
    setBlockings(b.data);
  }
  useEffect(() => { load(); }, []);

  const days = useMemo(() => {
    const start = monthStart;
    const end = endOfMonth(addMonths(monthStart, 1));
    const arr = [];
    let d = new Date(start);
    while (d <= end) {
      arr.push(new Date(d));
      d = addDays(d, 1);
    }
    return arr;
  }, [monthStart]);

  const rangeStart = days[0];
  const rangeEnd = days[days.length - 1];

  async function submitBlock() {
    if (!newBlock.house_id || !newBlock.range.from || !newBlock.range.to) {
      toast.error("Preencha casa e período");
      return;
    }
    try {
      await api.post("/admin/blockings", {
        house_id: newBlock.house_id,
        start_date: iso(newBlock.range.from),
        end_date: iso(newBlock.range.to),
        type: newBlock.type,
        note: newBlock.note,
      });
      toast.success("Bloqueio adicionado");
      setShowBlock(false);
      setNewBlock({ house_id: "", range: { from: undefined, to: undefined }, type: "manual", note: "" });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro");
    }
  }

  async function deleteBlock(id, isReservation) {
    if (isReservation) {
      toast.info("Cancele a reserva na tela de Reservas para liberar as datas.");
      return;
    }
    if (!confirm("Remover este bloqueio?")) return;
    try {
      await api.delete(`/admin/blockings/${id}`);
      toast.success("Bloqueio removido");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro");
    }
  }

  return (
    <div data-testid="admin-calendar-page">
      <div className="mb-8 flex items-end justify-between flex-wrap gap-3">
        <div>
          <span className="text-[11px] uppercase tracking-[0.28em] text-slate-500">Calendário multi-casa</span>
          <h1 className="text-3xl font-semibold text-[#0AA89C] mt-1">Ocupação</h1>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setMonthStart(addMonths(monthStart, -1))} data-testid="cal-prev">
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <div className="text-sm font-medium min-w-[180px] text-center">
            {format(monthStart, "LLLL yyyy", { locale: ptBR })} — {format(addMonths(monthStart, 1), "LLLL yyyy", { locale: ptBR })}
          </div>
          <Button variant="outline" size="sm" onClick={() => setMonthStart(addMonths(monthStart, 1))} data-testid="cal-next">
            <ChevronRight className="w-4 h-4" />
          </Button>
          <Button onClick={() => setShowBlock(true)} data-testid="add-block-btn" className="bg-[#0AA89C] hover:bg-[#066B63]">
            <Plus className="w-4 h-4 mr-1" /> Bloquear datas
          </Button>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-md overflow-x-auto">
        <div className="min-w-max">
          {/* Header row */}
          <div className="grid" style={{ gridTemplateColumns: `220px repeat(${days.length}, 28px)` }}>
            <div className="p-3 bg-slate-50 border-b border-slate-200 text-[10px] uppercase tracking-[0.24em] text-slate-500 font-semibold sticky left-0 z-10">
              Casa
            </div>
            {days.map((d, i) => {
              const isFirst = d.getDate() === 1;
              return (
                <div key={i} className={`text-center text-[10px] py-1 border-b border-slate-200 ${d.getDay() === 0 || d.getDay() === 6 ? "bg-slate-50" : ""}`}>
                  {isFirst ? <div className="font-semibold text-[#0AA89C]">{format(d, "MMM", { locale: ptBR })}</div> : null}
                  <div className="text-slate-600">{d.getDate()}</div>
                </div>
              );
            })}
          </div>

          {houses.map((h) => (
            <div key={h.id} className="grid relative" style={{ gridTemplateColumns: `220px repeat(${days.length}, 28px)` }}>
              <div className="p-3 border-b border-slate-100 text-sm sticky left-0 bg-white z-10">
                <div className="font-medium">{h.name}</div>
                <div className="text-[10px] text-slate-500">{h.city}</div>
              </div>
              {days.map((d, i) => (
                <div key={i} className={`h-14 border-b border-l border-slate-100 ${d.getDay() === 0 || d.getDay() === 6 ? "bg-slate-50/50" : ""}`} />
              ))}
              {/* Blockings overlay */}
              {blockings
                .filter((b) => b.house_id === h.id)
                .map((b) => {
                  const s = parseISO(b.start_date);
                  const e = parseISO(b.end_date);
                  if (e < rangeStart || s > rangeEnd) return null;
                  const startClamp = s < rangeStart ? rangeStart : s;
                  const endClamp = e > rangeEnd ? addDays(rangeEnd, 1) : e;
                  const offset = differenceInDays(startClamp, rangeStart);
                  const width = differenceInDays(endClamp, startClamp);
                  if (width <= 0) return null;
                  return (
                    <button
                      key={b.id}
                      onClick={() => deleteBlock(b.id, b.type === "reservation")}
                      title={`${TYPE_LABEL[b.type] || b.type} — ${b.note || ""}`}
                      className={`absolute top-2 h-10 rounded ${TYPE_COLOR[b.type] || "bg-slate-500"} text-white text-[10px] font-medium px-2 flex items-center overflow-hidden hover:opacity-80`}
                      style={{
                        left: `calc(220px + ${offset} * 28px + 2px)`,
                        width: `calc(${width} * 28px - 4px)`,
                      }}
                    >
                      <span className="truncate">{TYPE_LABEL[b.type] || b.type}</span>
                    </button>
                  );
                })}
            </div>
          ))}
        </div>
      </div>

      <div className="mt-6 flex flex-wrap gap-4 text-xs text-slate-600">
        {Object.entries(TYPE_LABEL).map(([k, v]) => (
          <div key={k} className="flex items-center gap-2">
            <span className={`inline-block w-3 h-3 rounded ${TYPE_COLOR[k]}`} />
            {v}
          </div>
        ))}
      </div>

      <Dialog open={showBlock} onOpenChange={setShowBlock}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle>Bloquear datas</DialogTitle>
            <DialogDescription>
              Utilize este bloqueio para períodos indisponíveis por manutenção ou uso próprio. Para reservas
              recebidas em canais externos (Airbnb/Booking), utilize a tela de Reservas.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Casa</Label>
              <Select value={newBlock.house_id} onValueChange={(v) => setNewBlock({ ...newBlock, house_id: v })}>
                <SelectTrigger data-testid="block-house-select"><SelectValue placeholder="Selecione a casa" /></SelectTrigger>
                <SelectContent className="bg-white">
                  {houses.map((h) => (
                    <SelectItem key={h.id} value={h.id}>{h.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Período</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    data-testid="block-date-trigger"
                    className="w-full text-left px-3 py-2 border border-[#E4E4E7] rounded-md flex items-center gap-3 text-sm hover:border-[#0AA89C]"
                  >
                    <CalendarDays className="w-4 h-4 text-[#0AA89C]" />
                    {newBlock.range.from
                      ? newBlock.range.to
                        ? `${format(newBlock.range.from, "d MMM", { locale: ptBR })} — ${format(newBlock.range.to, "d MMM", { locale: ptBR })}`
                        : `${format(newBlock.range.from, "d MMM", { locale: ptBR })} — ?`
                      : "Selecione o período"}
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 bg-white" align="start">
                  <Calendar
                    mode="range"
                    locale={ptBR}
                    numberOfMonths={2}
                    selected={newBlock.range}
                    onSelect={(v) => setNewBlock({ ...newBlock, range: v || { from: undefined, to: undefined } })}
                  />
                </PopoverContent>
              </Popover>
            </div>
            <div>
              <Label>Origem</Label>
              <Select value={newBlock.type} onValueChange={(v) => setNewBlock({ ...newBlock, type: v })}>
                <SelectTrigger data-testid="block-type"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="manual">Manual</SelectItem>
                  <SelectItem value="other">Outro</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Observação</Label>
              <Textarea value={newBlock.note} onChange={(e) => setNewBlock({ ...newBlock, note: e.target.value })} data-testid="block-note" />
            </div>
            <Button className="w-full bg-[#0AA89C] hover:bg-[#066B63]" onClick={submitBlock} data-testid="block-submit">
              Bloquear
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
